import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { BUSINESS } from "@/lib/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/why-us", label: "Why Us" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#0A0A0B]/90 backdrop-blur-md border-b border-[#C9A24B]/20"
          : "bg-[#0A0A0B]/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="header-logo"
            aria-label="Savage Cleaning home"
            className="font-serif-display text-xl sm:text-2xl tracking-wide text-white hover:text-[#C9A24B] transition-colors"
          >
            Savage <span className="text-[#C9A24B]">Cleaning</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="flex items-center gap-2 text-white/80 hover:text-[#C9A24B] text-sm tracking-wider"
              data-testid="header-phone"
            >
              <Phone size={16} strokeWidth={1.5} className="text-[#C9A24B]" />
              {BUSINESS.phone}
            </a>
            <Link to="/contact" className="btn-primary" data-testid="header-cta-quote">
              Get a Free Quote
            </Link>
          </div>

          {/* Mobile: always-visible email/quote pill + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <a
              href={`mailto:${BUSINESS.email}?subject=${encodeURIComponent("Free quote request — Savage Cleaning")}`}
              className="inline-flex items-center gap-2 bg-[#C9A24B] text-[#0A0A0B] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-[#E6C16B] active:translate-y-px transition-colors"
              data-testid="mobile-email-quote"
              aria-label="Email Savage Cleaning for a free quote"
            >
              <Mail size={14} strokeWidth={2} />
              <span className="flex flex-col leading-tight items-start">
                <span className="text-[9px] tracking-[0.18em] opacity-80">Free quote</span>
                <span className="text-[12px] tracking-wide">Email us</span>
              </span>
            </a>
            <button
              className="text-white p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        } bg-[#0A0A0B] border-t border-[#C9A24B]/20`}
        data-testid="mobile-menu"
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-serif-display text-3xl ${
                  isActive ? "text-[#C9A24B]" : "text-white"
                }`
              }
              data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
          <div className="pt-4 flex flex-col gap-4 border-t border-white/10">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="flex items-center gap-3 text-white/80"
              data-testid="mobile-phone"
            >
              <Phone size={18} className="text-[#C9A24B]" />
              {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn-primary w-full justify-center"
              data-testid="mobile-cta-quote"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
